import React, { useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, InfoWindow, LoadScriptNext, Polygon } from "@react-google-maps/api";
import {
  ArrowTopRightOnSquareIcon,
  BuildingStorefrontIcon,
  IdentificationIcon,
  MapIcon,
  MapPinIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { useDarkMode } from "../../contexts/darkModeContext";
import { salesCodeMapping } from "../GLOBAL/GlobalFunctions";

const MapComponent = ({
  geocodedAddresses,
  disabledUI,
  territoryPolygons = [],
  viewMode = "pins",
  selectedTerritoryCode,
  onTerritorySelect,
}) => {
  const [center] = useState({ lat: 37.6872, lng: -97.3301 });
  const [googleMaps, setGoogleMaps] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const mapRef = useRef(null);
  const markerClusterRef = useRef(null);
  const { darkMode } = useDarkMode();

  const apiKey = "AIzaSyDxrbk0CKmZUpJ-5eQGaxHH-I5lYeWrPfc";

  const validMarkers = useMemo(
    () => (geocodedAddresses || []).filter(
      (marker) => marker.lat && marker.lng && !isNaN(marker.lat) && !isNaN(marker.lng)
    ),
    [geocodedAddresses]
  );

  const selectedTerritory = useMemo(
    () => territoryPolygons.find((territory) => territory.salesCode === selectedTerritoryCode),
    [selectedTerritoryCode, territoryPolygons]
  );

  const territoryMarkers = useMemo(
    () => (selectedTerritory?.locations || []).filter(
      (marker) => marker.lat && marker.lng && !isNaN(marker.lat) && !isNaN(marker.lng)
    ),
    [selectedTerritory]
  );

  const visibleMarkers = viewMode === "territory" && selectedTerritory ? territoryMarkers : validMarkers;
  const canShowSelectedMarker = selectedMarker?.lat && selectedMarker?.lng && (
    selectedMarker.name || selectedMarker.customerCode || selectedMarker.address
  );

  const getCustomerAddress = (marker) => marker?.address || [
    marker?.address1,
    [marker?.city, marker?.state].filter(Boolean).join(", "),
    marker?.zip,
  ].filter(Boolean).join(" ");

  const getRepName = (marker) => salesCodeMapping[marker?.repCode] || salesCodeMapping[marker?.salesCode] || marker?.salesRep || "Unassigned";

  const getMapsUrl = (marker) => {
    const destination = getCustomerAddress(marker) || `${marker?.lat},${marker?.lng}`;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(destination)}`;
  };

  useEffect(() => {
    if (!googleMaps || !mapRef.current) return;

    if (markerClusterRef.current) {
      markerClusterRef.current.clearMarkers();
      markerClusterRef.current = null;
    }

    if (viewMode !== "pins" && !selectedTerritory) return;
    if (visibleMarkers.length === 0) return;

    const markers = visibleMarkers.map((marker) => {
      const newMarker = new googleMaps.maps.Marker({
        position: { lat: marker.lat, lng: marker.lng },
        title: marker.name,
        label: {
          text: marker.repCode || "",
          color: "white",
          fontSize: "11px",
          fontWeight: "700",
        },
        icon: {
          path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z",
          fillColor: selectedTerritory?.color || "#047857",
          fillOpacity: 1,
          strokeWeight: 2,
          strokeColor: "#ffffff",
          scale: 1.45,
          anchor: new googleMaps.maps.Point(12, 24),
          labelOrigin: new googleMaps.maps.Point(12, 10),
        },
      });

      newMarker.addListener("click", () => {
        if (marker?.lat && marker?.lng) {
          setSelectedMarker(marker);
        }
      });

      return newMarker;
    });

    markerClusterRef.current = new MarkerClusterer({ map: mapRef.current, markers });
  }, [googleMaps, selectedTerritory, validMarkers, viewMode, visibleMarkers]);

  useEffect(() => {
    setSelectedMarker(null);
  }, [selectedTerritoryCode, viewMode, geocodedAddresses]);

  useEffect(() => {
    if (!googleMaps || !mapRef.current) return;

    const bounds = new googleMaps.maps.LatLngBounds();
    let hasBounds = false;

    if (viewMode === "territory" && selectedTerritory?.path?.length) {
      selectedTerritory.path.forEach((point) => {
        bounds.extend(point);
        hasBounds = true;
      });
    } else {
      validMarkers.forEach((marker) => {
        bounds.extend({ lat: marker.lat, lng: marker.lng });
        hasBounds = true;
      });
    }

    if (hasBounds) {
      mapRef.current.fitBounds(bounds, 72);
    }
  }, [googleMaps, selectedTerritory, validMarkers, territoryPolygons, viewMode]);

  const handleLoad = (map) => {
    mapRef.current = map;
    setGoogleMaps(window.google);
  };

  if (!center) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-2 text-center ${darkMode ? "border-neutral-700" : ""}`}>
          <MapIcon className="size-12" />
          <h3 className="mt-2 text-sm font-semibold">Error Loading Map</h3>
          <p className="mt-1 text-sm text-gray-500">Check the customer address.</p>
        </div>
      </div>
    );
  }

  return (
    <LoadScriptNext googleMapsApiKey={apiKey}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={center}
        zoom={validMarkers.length > 1 ? 5 : 16}
        options={{
          mapTypeId: "satellite",
          disableDefaultUI: disabledUI,
          zoomControl: true,
          fullscreenControl: false,
          streetViewControl: false,
          mapTypeControl: false,
          colorScheme: `${darkMode ? "DARK" : "LIGHT"}`,
        }}
        onLoad={handleLoad}
      >
        {viewMode === "territory" && territoryPolygons.map((territory) => (
          <Polygon
            key={territory.salesCode}
            paths={territory.path}
            onClick={() => {
              setSelectedMarker(null);
              onTerritorySelect?.(territory.salesCode);
            }}
            options={{
              fillColor: territory.color,
              fillOpacity: selectedTerritoryCode === territory.salesCode ? 0.36 : 0.22,
              strokeColor: territory.color,
              strokeOpacity: 0.95,
              strokeWeight: selectedTerritoryCode === territory.salesCode ? 4 : 2,
              clickable: true,
              zIndex: selectedTerritoryCode === territory.salesCode ? 3 : 1,
            }}
          />
        ))}

        {canShowSelectedMarker && (
          <InfoWindow
            position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
            onCloseClick={() => setSelectedMarker(null)}
          >
            <div className="w-72 overflow-hidden rounded-lg bg-white text-neutral-950 shadow-xl">
              <div className="border-b border-neutral-100 bg-neutral-50 px-4 py-3">
                <div className="flex items-start gap-3">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-emerald-900 text-white">
                    <BuildingStorefrontIcon className="size-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-bold">{selectedMarker.name || "Unnamed customer"}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-neutral-500">
                      <span className="rounded bg-white px-1.5 py-0.5 font-semibold text-neutral-700 ring-1 ring-neutral-200">
                        {selectedMarker.customerCode || "No code"}
                      </span>
                      <span>{selectedMarker.repCode || selectedMarker.salesCode || "No rep"}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3 px-4 py-3 text-xs">
                <div className="flex gap-2">
                  <UserCircleIcon className="mt-0.5 size-4 shrink-0 text-emerald-900" />
                  <div className="min-w-0">
                    <div className="font-semibold text-neutral-800">{getRepName(selectedMarker)}</div>
                    <div className="text-neutral-500">Sales territory owner</div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <MapPinIcon className="mt-0.5 size-4 shrink-0 text-emerald-900" />
                  <div className="min-w-0">
                    <div className="line-clamp-2 font-medium text-neutral-800">{getCustomerAddress(selectedMarker) || "No address available"}</div>
                    <div className="text-neutral-500">
                      {Number(selectedMarker.lat).toFixed(4)}, {Number(selectedMarker.lng).toFixed(4)}
                    </div>
                  </div>
                </div>

                {(selectedMarker.city || selectedMarker.state) && (
                  <div className="flex gap-2">
                    <IdentificationIcon className="mt-0.5 size-4 shrink-0 text-emerald-900" />
                    <div className="min-w-0 font-medium text-neutral-800">
                      {[selectedMarker.city, selectedMarker.state, selectedMarker.zip].filter(Boolean).join(", ")}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex border-t border-neutral-100 bg-neutral-50 px-4 py-3">
                <a
                  href={getMapsUrl(selectedMarker)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-8 w-full items-center justify-center gap-2 rounded-md bg-emerald-900 px-3 text-xs font-semibold text-white hover:bg-emerald-800"
                >
                  Open in Maps
                  <ArrowTopRightOnSquareIcon className="size-4" />
                </a>
              </div>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </LoadScriptNext>
  );
};

export default MapComponent;
